/**
 * IapUpgradeButton — one-tap StoreKit subscription purchase at the
 * moment a user hits a plan gate (iOS native only). Falls back to a
 * plain link to the plans page anywhere StoreKit isn't available.
 * Includes the 3.1.2 auto-renew disclosure under the buy button.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { SparkleIcon as Sparkle } from "@phosphor-icons/react";
import { iapSupported, getIapPackages, purchaseIapPackage } from "@/lib/revenuecat";
import { haptic } from "@/lib/native";
import { maybeRequestAppReview } from "@/lib/appReview";

export default function IapUpgradeButton({
  packageIds,
  label,
  context = "generic",
  fallbackTo = "/account/subscription",
  fallbackLabel = "See plans",
  className = "",
  onPurchased,
}) {
  const [pkg, setPkg] = useState(null);
  const [userId, setUserId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!iapSupported()) return;
    let cancelled = false;
    (async () => {
      try {
        const me = await api.get("/auth/me", { silent: true });
        if (cancelled || !me?.data?.id) return;
        setUserId(me.data.id);
        const pkgs = await getIapPackages(me.data.id);
        const found = packageIds.map((id) => pkgs.find((p) => p.identifier === id)).find(Boolean);
        if (!cancelled && found) {
          setPkg(found);
          track("native_upgrade_prompt_seen", { context, package: found.identifier });
        }
      } catch {}
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context]);

  const buy = async () => {
    if (busy) return;
    setBusy(true);
    track("native_upgrade_purchase_started", { context, package: pkg.identifier });
    try {
      await purchaseIapPackage(userId, pkg);
      haptic("medium");
      track("native_upgrade_purchase_succeeded", { context, package: pkg.identifier });
      toast.success("Purchase complete — your plan is now active.");
      maybeRequestAppReview("subscription_purchase");
      if (onPurchased) onPurchased();
      else setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      const msg = String(e?.message || "");
      if (!/cancel/i.test(msg) && !e?.userCancelled) {
        track("native_upgrade_purchase_failed", { context, package: pkg.identifier });
        toast.error(msg || "Purchase didn't complete. You were not charged.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (!pkg) {
    return (
      <Link
        to={fallbackTo}
        className={`inline-flex items-center justify-center rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white h-11 px-6 text-[14px] font-semibold transition-colors ${className}`}
        data-testid={`iap-upgrade-fallback-${context}`}
      >
        <Sparkle size={13} weight="duotone" className="mr-1.5" />
        {fallbackLabel}
      </Link>
    );
  }

  const annual = /annual|yearly/i.test(pkg.identifier);
  return (
    <div className={className}>
      <button
        type="button"
        onClick={buy}
        disabled={busy}
        className="w-full rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] disabled:opacity-60 text-white h-11 px-6 text-[14px] font-semibold transition-colors"
        data-testid={`iap-upgrade-buy-${context}`}
      >
        <Sparkle size={13} weight="duotone" className="inline mr-1.5 -mt-0.5" />
        {busy ? "Processing…" : `${label} — ${pkg.product?.priceString || ""}${annual ? "/yr" : "/mo"}`}
      </button>
      <p className="text-[10.5px] text-[#9CA3AF] mt-1.5 text-center leading-snug">
        Billed via Apple. Auto-renews {annual ? "yearly" : "monthly"} until cancelled in
        Settings → Subscriptions. <Link to="/terms" className="underline">Terms</Link> ·{" "}
        <Link to="/privacy" className="underline">Privacy</Link>
      </p>
    </div>
  );
}
